import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Camera, 
  Calendar,
  ArrowLeft,
  Trash2,
  Eye,
  Clock,
  Activity,
  ZapOff,
  Zap,
  Image as ImageIcon,
  Loader2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { todayInTimezone, formatUtcTime } from '@/utils/dateUtils';
import { useLanguage } from '@/i18n/LanguageContext';

// Helper to construct thumbnail URL from image_path
const getScreenshotUrl = (screenshot) => {
  if (screenshot.image_path) {
    // image_path is like: /uploads/screenshots/{company_id}/{staff_id}/{filename}
    // Serve via the API route
    return `${API}${screenshot.image_path}`;
  }
  return null;
};

function StaffScreenshotsPage() {
  const { t, isRTL } = useLanguage();
  const { staffId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [screenshots, setScreenshots] = useState([]);
  const [stats, setStats] = useState({ total_screenshots: 0, idle_count: 0, active_count: 0 });
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  const [page, setPage] = useState(1);
  const limit = 50;

  const [staffName, setStaffName] = useState('');
  const [loading, setLoading] = useState(true);
  const [companyTimezone, setCompanyTimezone] = useState('UTC');
  const [selectedDate, setSelectedDate] = useState(searchParams.get('date') || todayInTimezone('UTC'));

  useEffect(() => {
    setSelectedDate((prev) => searchParams.get('date') || todayInTimezone(companyTimezone));
  }, [companyTimezone, searchParams]);
  const [selectedScreenshots, setSelectedScreenshots] = useState([]);
  const [viewingScreenshot, setViewingScreenshot] = useState(null);
  const [viewingImageUrl, setViewingImageUrl] = useState(null);
  const [viewingImageLoading, setViewingImageLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchScreenshots();
  }, [staffId, selectedDate, page]);

  useEffect(() => {
    setPage(1);
  }, [staffId, selectedDate]);

  useEffect(() => {
    const loadCompanyTimezone = async () => {
      try {
        const res = await axios.get(`${API}/admin/settings/company`, { withCredentials: true });
        setCompanyTimezone(res.data?.timezone || 'UTC');
      } catch (e) {
        // ignore
      }
    };
    loadCompanyTimezone();
  }, []);

  useEffect(() => {
    return () => {
      if (viewingImageUrl) {
        URL.revokeObjectURL(viewingImageUrl);
      }
    };
  }, [viewingImageUrl]);

  const fetchScreenshots = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/activities/screenshots/${staffId}`, {
        params: { date: selectedDate, page, limit },
        withCredentials: true
      });
      setScreenshots(response.data.screenshots || []);
      setStats(response.data.stats || { total_screenshots: 0, idle_count: 0, active_count: 0 });
      setPagination(response.data.pagination || { page, limit, total: 0, pages: 0 });
      setStaffName(response.data.staff_name || '');
      setSelectedScreenshots([]);
    } catch (error) {
      console.error('Failed to fetch screenshots:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedScreenshots(screenshots.map(s => s.screenshot_id));
    } else {
      setSelectedScreenshots([]);
    }
  };

  const openScreenshotViewer = async (screenshot) => {
    setViewingScreenshot(screenshot);
    setViewingImageUrl(null);
    setViewingImageLoading(true);

    try {
      const res = await axios.get(
        `${API}/admin/activities/screenshots/${screenshot.screenshot_id}/image`,
        { withCredentials: true, responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      setViewingImageUrl(url);
    } catch (e) {
      console.error('Failed to load screenshot image:', e);
    } finally {
      setViewingImageLoading(false);
    }
  };

  const closeScreenshotViewer = () => {
    if (viewingImageUrl) {
      URL.revokeObjectURL(viewingImageUrl);
    }
    setViewingImageUrl(null);
    setViewingImageLoading(false);
    setViewingScreenshot(null);
  };

  // Navigate to previous/next screenshot
  const getCurrentIndex = () => {
    if (!viewingScreenshot) return -1;
    return screenshots.findIndex(s => s.screenshot_id === viewingScreenshot.screenshot_id);
  };

  const navigateScreenshot = async (direction) => {
    const currentIndex = getCurrentIndex();
    if (currentIndex === -1) return;

    let newIndex;
    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : screenshots.length - 1;
    } else {
      newIndex = currentIndex < screenshots.length - 1 ? currentIndex + 1 : 0;
    }

    const newScreenshot = screenshots[newIndex];
    if (newScreenshot) {
      // Clean up old URL
      if (viewingImageUrl) {
        URL.revokeObjectURL(viewingImageUrl);
      }
      await openScreenshotViewer(newScreenshot);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!viewingScreenshot) return;
      
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        navigateScreenshot('prev');
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        navigateScreenshot('next');
      } else if (e.key === 'Escape') {
        closeScreenshotViewer();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [viewingScreenshot, screenshots, viewingImageUrl]);

  const handleSelectScreenshot = (screenshotId, checked) => {
    if (checked) {
      setSelectedScreenshots([...selectedScreenshots, screenshotId]);
    } else {
      setSelectedScreenshots(selectedScreenshots.filter(id => id !== screenshotId));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedScreenshots.length === 0) return;
    
    setDeleting(true);
    try {
      await axios.post(`${API}/admin/activities/screenshots/bulk-delete`, {
        screenshot_ids: selectedScreenshots
      }, { withCredentials: true });
      
      await fetchScreenshots();
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error('Failed to delete screenshots:', error);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteSingle = async (screenshotId) => {
    try {
      await axios.delete(`${API}/admin/activities/screenshots/${screenshotId}`, {
        withCredentials: true
      });
      await fetchScreenshots();
    } catch (error) {
      console.error('Failed to delete screenshot:', error);
    }
  };

    const formatTime = (value) => formatUtcTime(value);


  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const pieData = [
    { name: 'Active', value: stats.active_count, color: '#10B981' },
    { name: 'Idle', value: stats.idle_count, color: '#F59E0B' }
  ].filter(d => d.value > 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/admin/activities')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Camera className="w-6 h-6 text-pink-600" />
              Screenshots - {staffName}
            </h2>
            <p className="text-gray-600">View and manage staff screenshots</p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-gray-500" />
            <Input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-40"
              data-testid="date-picker"
            />
          </div>
          {selectedScreenshots.length > 0 && (
            <Button 
              variant="destructive"
              onClick={() => setDeleteDialogOpen(true)}
              data-testid="delete-selected-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete ({selectedScreenshots.length})
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-pink-500 to-rose-500 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-pink-100 text-sm">Total Screenshots</p>
                <p className="text-3xl font-bold">{stats.total_screenshots}</p>
              </div>
              <Camera className="w-10 h-10 text-pink-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-indigo-500 to-purple-500 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-100 text-sm">{t("common.active")}</p>
                <p className="text-3xl font-bold">{stats.active_count}</p>
              </div>
              <Zap className="w-10 h-10 text-indigo-200" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-500 to-orange-500 text-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm">Idle</p>
                <p className="text-3xl font-bold">{stats.idle_count}</p>
              </div>
              <ZapOff className="w-10 h-10 text-amber-200" />
            </div>
          </CardContent>
        </Card>
        
        {/* Pie Chart Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="h-24">
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={25}
                      outerRadius={40}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400">
                  <Activity className="w-8 h-8" />
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Screenshots Grid */}
      <Card>
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Screenshots</CardTitle>
              <CardDescription>Click on a screenshot to view full size (loads on demand)</CardDescription>
            </div>
            {screenshots.length > 0 && (
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={selectedScreenshots.length === screenshots.length}
                  onCheckedChange={handleSelectAll}
                  data-testid="select-all-checkbox"
                />
                <span className="text-sm text-gray-500">Select All</span>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
            </div>
          ) : screenshots.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <ImageIcon className="w-16 h-16 mb-4 opacity-50" />
              <p className="text-lg">No screenshots for this date</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {screenshots.map((screenshot) => (
                <div 
                  key={screenshot.screenshot_id}
                  className="relative group rounded-lg overflow-hidden border hover:shadow-lg transition-shadow"
                >
                  {/* Checkbox */}
                  <div className="absolute top-2 left-2 z-10">
                    <Checkbox
                      checked={selectedScreenshots.includes(screenshot.screenshot_id)}
                      onCheckedChange={(checked) => handleSelectScreenshot(screenshot.screenshot_id, checked)}
                      className="bg-white"
                      data-testid={`checkbox-${screenshot.screenshot_id}`}
                    />
                  </div>
                  
                  {/* Status Badge */}
                  <div className={`absolute top-2 right-2 z-10 px-2 py-0.5 rounded text-xs font-medium ${
                    screenshot.screen_status === 'idle' 
                      ? 'bg-amber-100 text-amber-800' 
                      : 'bg-indigo-100 text-indigo-800'
                  }`}>
                    {screenshot.screen_status === 'idle' ? 'Idle' : 'Active'}
                  </div>
                  
                  {/* Screenshot Image */}
                  <div 
                    className="aspect-video bg-gray-100 cursor-pointer relative overflow-hidden"
                    onClick={() => openScreenshotViewer(screenshot)}
                  >
                    {getScreenshotUrl(screenshot) ? (
                      <img
                        src={getScreenshotUrl(screenshot)}
                        alt={`Screenshot at ${formatTime(screenshot.timestamp)}`}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div 
                      className={`w-full h-full items-center justify-center absolute inset-0 bg-gray-100 ${getScreenshotUrl(screenshot) ? 'hidden' : 'flex'}`}
                    >
                      <ImageIcon className="w-8 h-8 text-gray-300" />
                    </div>
                  </div>
                  
                  {/* Info */}
                  <div className="p-2 bg-white">
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(screenshot.timestamp)}
                    </p>
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatTime(screenshot.timestamp)}
                    </p>
                  </div>
                  
                  {/* Delete button on hover */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="destructive"
                      size="sm"
                      className="w-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSingle(screenshot.screenshot_id);
                      }}
                      data-testid={`delete-btn-${screenshot.screenshot_id}`}
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>


      {/* Pagination */}
      {!loading && pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Showing {(pagination.total === 0) ? 0 : ((page - 1) * limit + 1)}-
            {Math.min(page * limit, pagination.total)} of {pagination.total}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page <= 1}
              data-testid="pagination-prev"
            >
              Prev
            </Button>
            <span className="text-sm text-gray-700">
              Page {page} of {pagination.pages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(Math.min(pagination.pages, page + 1))}
              disabled={page >= pagination.pages}
              data-testid="pagination-next"
            >
              Next
            </Button>
          </div>
        </div>
      )}



 


      {/* View Screenshot Modal */}
      <Dialog open={!!viewingScreenshot} onOpenChange={(open) => { if (!open) closeScreenshotViewer(); }}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Camera className="w-5 h-5 text-pink-600" />
                Screenshot Details
              </span>
              <span className="text-sm font-normal text-gray-500">
                {getCurrentIndex() + 1} of {screenshots.length}
              </span>
            </DialogTitle>
          </DialogHeader>
          {viewingScreenshot && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatDate(viewingScreenshot.timestamp)}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {formatTime(viewingScreenshot.timestamp)}
                </span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  viewingScreenshot.screen_status === 'idle' 
                    ? 'bg-amber-100 text-amber-800' 
                    : 'bg-indigo-100 text-indigo-800'
                }`}>
                  {viewingScreenshot.screen_status === 'idle' ? 'Idle' : 'Active'}
                </span>
              </div>
              
              {/* Image with navigation arrows */}
              <div className="relative">
                {/* Left Arrow */}
                <button
                  onClick={() => navigateScreenshot('prev')}
                  className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                  title="Previous screenshot (←)"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                
                {/* Right Arrow */}
                <button
                  onClick={() => navigateScreenshot('next')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-colors"
                  title="Next screenshot (→)"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
                
                {/* Screenshot Image */}
                <div className="border rounded-lg overflow-hidden bg-gray-100">
                  {viewingImageLoading ? (
                    <div className="h-[500px] flex items-center justify-center">
                      <Loader2 className="w-10 h-10 animate-spin text-pink-600" />
                    </div>
                  ) : viewingImageUrl ? (
                    <img 
                      src={viewingImageUrl}
                      alt="Screenshot"
                      className="w-full max-h-[70vh] object-contain"
                    />
                  ) : (
                    <div className="h-[500px] flex items-center justify-center">
                      <ImageIcon className="w-16 h-16 text-gray-300" />
                    </div>
                  )}
                </div>
              </div>
              
              {/* Navigation hint */}
              <p className="text-xs text-center text-gray-400">
                Use ← → arrow keys or click arrows to navigate
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Screenshots</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedScreenshots.length} selected screenshots? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteSelected}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default StaffScreenshotsPage;
